<template>
  <v-card>
    <v-row no-gutters justify="center">
      <v-col cols="12">
        <p class="text-subtitle-1 text-widget-color font-weight-bold">
          {{ $t("cart-detail.title") }}
        </p>
      </v-col>

      <v-col v-if="!cart.length" cols="12">
        <p class="text-subtitle-2 text-black font-weight-regular">
          {{ $t("cart-detail.desc") }}
        </p>
      </v-col>

      <div v-else-if="cart.length">
        <v-card width="auto" variant="outlined" color="widget-color" class="pa-2 mb-2">
          <v-row dense>
            <template v-for="cartItem in cart" :key="cartItem">
              <v-col cols="6">
                <p class="text-subtitle-2 text-black">
                  {{ cartItem.productType.producttype_name }}
                </p>
              </v-col>

              <v-col cols="6">
                <p class="text-subtitle-2 text-black text-right">
                  {{
                    `(${cartItem.qty} x ${numberToRupiah((cartItem.productType.discounted_price !== null &&
                      cartItem.productType.discounted_price >= 0 ? cartItem.productType.discounted_price :
                      cartItem.productType.producttype_price))})`
                  }}
                </p>
              </v-col>

              <v-col cols="6">
                <p class="text-subtitle-2 text-black">
                  {{ $t("cart-detail.ticket-fee") }}
                </p>
              </v-col>

              <v-col cols="6">
                <p class="text-subtitle-2 text-black text-right">
                  {{
                    `${numberToRupiah(cartItem.qty * (cartItem.productType.discounted_price !== null &&
                      cartItem.productType.discounted_price >= 0 ? cartItem.productType.discounted_price :
                      cartItem.productType.producttype_price))}`
                  }}
                </p>
              </v-col>

              <v-col v-if="discountVoucher && responseVoucher?.producttype_id == cartItem.productType.producttype_id"
                cols="6" align-self="center" class="text-left">
                <p class="text-subtitle-2 text-red">
                  {{ $t("cart-detail.discount") }}
                </p>
              </v-col>

              <v-spacer></v-spacer>

              <v-col v-if="discountVoucher && responseVoucher?.producttype_id == cartItem.productType.producttype_id"
                cols="6" align-self="center" class="text-right">
                <p class="text-subtitle-2 text-red">
                  {{ `-${numberToRupiah(this.discountVoucher)}` }}
                </p>
              </v-col>

              <v-col cols="6">
                <p class="text-subtitle-2 text-black font-weight-bold">
                  {{ $t("cart-detail.subtotal") }}
                </p>
              </v-col>

              <v-col cols="6">
                <p class="text-subtitle-2 text-black font-weight-bold text-right">
                  {{ numberToRupiah(subtotal(cartItem)) }}
                </p>
              </v-col>

              <v-divider :thickness="2"></v-divider>
            </template>

            <v-col v-if="cart ? cart.length : false" cols="6" align-self="center" class="text-left">
              <p class="text-subtitle-2 text-black font-weight-bold">
                {{ $t("cart-detail.total-purchase") }}
              </p>
            </v-col>

            <v-col v-if="(cart ? cart.length : false)" cols="6" align-self="center" class="text-right">
              <p class="text-subtitle-2 text-black font-weight-bold">
                {{ numberToRupiah(totalCartPrice) }}
              </p>
            </v-col>

            <v-col v-if="cart ? cart.length : false" cols="6">
              <div class="text-subtitle-2 text-black">
                {{ $t("cart-detail.service-fee") }}
              </div>
            </v-col>

            <v-col v-if="cart ? cart.length : false" cols="6">
              <p class="text-subtitle-2 text-black text-right">
                {{ numberToRupiah(serviceFee) }}
              </p>
            </v-col>

            <v-col v-if="selectedPaymentMethod" cols="6">
              <p class="text-subtitle-2 text-black">
                {{ $t("cart-detail.payment-fee") }}
              </p>
            </v-col>

            <v-col v-if="selectedPaymentMethod" cols="6">
              <p class="text-subtitle-2 text-black text-right">
                {{ numberToRupiah(paymentFee) }}
              </p>
            </v-col>

            <v-divider :thickness="2"></v-divider>

            <v-col cols="6" align-self="center" class="text-left">
              <p class="text-subtitle-2 text-black font-weight-bold">
                {{ `${$t("cart-detail.total-tickets")}${!selectedPaymentMethod ? "*" : ""}` }}
              </p>
            </v-col>

            <v-spacer></v-spacer>

            <v-col v-if="cart ? cart.length : false" cols="6" align-self="center" class="text-right">
              <p class="text-subtitle-2 text-black font-weight-bold">
                {{ numberToRupiah(totalCartPrice + serviceFee + paymentFee) }}
              </p>
            </v-col>
          </v-row>
        </v-card>
        <div v-if="!selectedPaymentMethod" class="text-caption">
          {{ `*${$t("cart-detail.payment-fee-desc")}` }}
        </div>

        <v-card v-if="currentStep === step.PAYMENT_PRODUCT" width="auto" variant="outlined" color="widget-color"
          class="pa-2 mt-2">
          <v-row dense>
            <v-col cols="12">
              <p class="text-subtitle-2 text-black font-weight-bold">
                {{ $t("cart-detail.payment-method") }}
              </p>
            </v-col>

            <v-divider :thickness="2"></v-divider>

            <v-col cols="12">
              <p class="text-subtitle-2 text-black">
                {{
                  !selectedPaymentMethod || isFree
                    ? "-"
                    : selectedPaymentMethod.payment_name
                }}
              </p>
            </v-col>
          </v-row>
        </v-card>

        <v-text-field v-if="currentStep === step.PAYMENT_PRODUCT" placeholder="Voucher" class="mt-2"
          v-model="voucherCode" variant="outlined" density="compact" hide-details color="primary"
          :readonly="discountVoucher">
        </v-text-field>

        <v-btn v-if="currentStep === step.PAYMENT_PRODUCT" class="text-none text-caption mt-2" @click="checkVoucher"
          :disabled="discountVoucher" color="widget-color" rounded="lg">{{ $t("cart-detail.redeem-voucher")
          }}</v-btn>

        <v-switch v-if="currentStep === step.PAYMENT_PRODUCT" v-model="sendTexter" color="primary" hide-details>
          <template #label>
            <div class="text-subtitle-2">{{ $t("cart-detail.send-whatsapp") }}</div>
          </template>
        </v-switch>
      </div>

      <v-col cols="12" align-self="center" class="pa-0 mt-2">
        <v-btn v-if="currentStep == step.CUSTOMER_INFORMATION && formFounded" :disabled="!valid"
          @click="this.$store.state.formStep--" rounded="lg" block variant="tonal" class="mb-2 text-none text-caption">
          {{ $t("cart-detail.button.edit-data") }}
        </v-btn>

        <v-btn v-if="cart.length && validation.quantity.maxBuy(product.is_rombongan, totalCartQty)" color="error"
          variant="flat" disabled rounded="lg" class="text-center text-none text-caption" block>
          {{ $t("cart-detail.max-ticket-warning") }}
        </v-btn>

        <v-btn v-else-if="currentStep == step.PRODUCT_PREVIEW" class="text-none text-caption"
          @click="validateRequiredInput()" rounded="lg" block color="widget-color"
          :disabled="cart.length <= 0 || (!validateInput && product.is_rombongan)">
          {{ $t("cart-detail.button.continue") }}
        </v-btn>

        <v-btn v-else-if="currentStep == step.CUSTOMER_INFORMATION" class="text-none text-caption"
          @click="validateRequiredFormCheckbox()" rounded="lg" block color="widget-color" :disabled="(currentStep === step.CUSTOMER_INFORMATION && !valid)
            ">
          {{ $t("cart-detail.button.continue") }}
        </v-btn>

        <v-btn v-else-if="
          currentStep === step.PAYMENT_PRODUCT && (orderStatus === null || orderStatus === 'error')
        " block rounded="lg" class="text-none text-caption" color="widget-color" @click="dialogConfirmation = true"
          :loading="dialogConfirmation || loadingOrder" :disabled="currentStep === step.PAYMENT_PRODUCT && (!isFree && !selectedPaymentMethod)
            ">
          {{ $t("cart-detail.button.pay") }}
        </v-btn>

        <div v-else-if="
          currentStep === step.PAYMENT_PRODUCT && orderStatus === 'process'
        ">
          <v-btn @click="openPaymentWindow(paymentURL, !checkApp)" v-if="paymentStatus?.code !== '00'" block
            color="widget-color" rounded="lg" class="text-none text-caption">
            {{ $t("cart-detail.button.payment-page") }}
          </v-btn>

          <v-btn @click="checkTransactionStatus()" block rounded="lg" color="grey" class="text-none text-caption mt-2">
            {{ $t("cart-detail.button.payment-status") }}
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <v-card-item v-if="paymentStatus.show">
      <v-alert v-model="paymentStatus.show">
        <div v-if="paymentStatus.code === '01'">
          {{ $t("cart-detail.payment-status.process") }}
        </div>
      </v-alert>
    </v-card-item>

    <v-card-item v-if="orderStatus === 'error'">
      <v-alert type="error">
        {{ errorMessage }}
      </v-alert>
    </v-card-item>
  </v-card>

  <confirmation-view v-model:dialog="dialogConfirmation" v-model:confirmation="confirmation"></confirmation-view>

  <v-snackbar v-model="snackbar.show" rounded="pill" location="top" color="white" width="auto" min-width="auto"
    :timeout="2000">
    <div :class="snackbar.error ? 'text-error' : 'text-primary'" class="font-weight-bold">
      <v-icon :icon="snackbar.error ? 'mdi-close-circle' : 'mdi-check-circle'"
        :color="snackbar.error ? 'error' : 'primary'"></v-icon>
      {{ snackbar.text }}
    </div>
  </v-snackbar>
</template>
<script>
import ConfirmationView from "@/components/confirmation/Confirmation.vue";
import { step } from "@/config/step";
import { numberToRupiah } from "@/utils/numberFormatter";
import * as validation from "@/utils/validation";
import moment from "moment-timezone";
import { PAYMENT } from "@/config/payment";

export default {
  components: { ConfirmationView },

  data() {
    return {
      validation: {
        quantity: validation.default.quantity,
        cart: validation.default.cart,
      },
      step: step,
      dialogConfirmation: false,
      confirmation: false,
      loadingOrder: false,
      merchantCode: null,
      merchantOrderId: null,
      orderStatus: null,
      errorMessage: "",
      paymentStatus: {
        show: false,
        code: "",
      },
      paymentURL: "",
      dialog: {
        state: false,
        text: ""
      },
      voucherCode: undefined,
      responseVoucher: null,
      snackbar: {
        show: false,
        text: null,
      },
      sendTexter: false,
    };
  },

  computed: {
    valid() {
      return this.$store.state.formCount == (this.$store.state.formStep ?? 0) && !this.$store.state.loadingForm
    },
    validateInput() {
      return this.$store.state.requiredInput
    },
    formCount() {
      return this.$store.state.formCount
    },
    product() {
      return this.$store.state.product;
    },
    cart() {
      return this.$store.getters.sortedCartByTypeID;
    },
    formFounded() {
      return this.cart.some((cartItem) => cartItem.productType.form)
    },
    totalCartQty() {
      return this.$store.getters.totalCartQty;
    },
    currentStep() {
      return this.$store.state.step;
    },
    totalCartPrice() {
      return this.$store.getters.totalCartPrice
    },
    serviceFee() {
      let total = 0

      for (const cartItem of this.cart) {
        let totalPlatformFee = 0
        const platformFee = cartItem.productType.platform_fee_category ?? cartItem.productType.platform_fee_store ?? cartItem.productType.platform_fee_general
        const productTypeTotal = (cartItem.productType.discounted_price !== null && cartItem.productType.discounted_price >= 0 ? cartItem.productType.discounted_price : cartItem.productType.producttype_price) * cartItem.qty
        const totalFlatFee = (cartItem.qty * platformFee.flat_fee) || 0

        if ((this.responseVoucher?.producttype_id == cartItem.productType.producttype_id && this.responseVoucher?.voucher.is_free) || this.discountVoucher >= productTypeTotal) {
          totalPlatformFee = 0
        } else if (this.responseVoucher?.producttype_id == cartItem.productType.producttype_id && this.discountVoucher) {
          const discount = productTypeTotal - this.discountVoucher
          totalPlatformFee = discount <= 0 ? 0 : Math.round(discount * (platformFee.percentage / 100) + totalFlatFee)
        } else {
          totalPlatformFee = Math.round(productTypeTotal * (platformFee.percentage / 100) + totalFlatFee)
        }

        total += totalPlatformFee
      }

      return total
    },
    paymentFee() {
      return this.isFree ? 0 : parseInt(this.selectedPaymentMethod?.totalFee || 0);
    },
    isFree() {
      return this.totalCartPrice <= 0;
    },
    selectedPaymentMethod() {
      return this.$store.getters.selectedPaymentMethod;
    },
    discountVoucher() {
      return this.$store.state.discount
    },
    appId() {
      return this.$store.state.appId
    },
    appSetting() {
      return this.$store.state.appSetting
    },
    integrator() {
      return this.$store.state.integrator
    },
    paymentMethodsIntegrator() {
      return this.$store.state.paymentMethodsIntegrator
    },
    checkApp() {
      return this.appId && this.appSetting && this.paymentMethodsIntegrator.length && this.integrator.length && this.appSetting.paymentmethod_id.map(Number).includes(this.selectedPaymentMethod?.paymentmethod_id) && this.paymentMethodsIntegrator.some(method => method.paymentmethod_id == this.selectedPaymentMethod?.paymentmethod_id)
    }
  },
  watch: {
    orderStatus(value) {
      this.$store.state.orderStatus = value;
    },

    confirmation: {
      handler(value) {

        if (value) {
          this.order();
        }
      }
    }
  },
  methods: {
    next() {
      this.$store.commit("nextStep");
    },
    platformFee(product) {
      const platformFee = product.productType.platform_fee_category ?? product.productType.platform_fee_store ?? product.productType.platform_fee_general
      const productTypeTotal = product.productType.producttype_price * product.qty
      const totalFlatFee = (product.qty * platformFee.flat_fee) || 0
      let total = productTypeTotal

      if (this.responseVoucher?.producttype_id == product.productType.producttype_id && (this.responseVoucher?.voucher.is_free || this.discountVoucher == productTypeTotal)) {
        total = 0
      } else if (this.responseVoucher?.producttype_id == product.productType.producttype_id && this.discountVoucher) {
        const discount = total - this.discountVoucher
        total = Math.round(discount * (platformFee.percentage / 100) + totalFlatFee)
      } else {
        total = Math.round(productTypeTotal * (platformFee.percentage / 100) + totalFlatFee)
      }

      return total
    },
    subtotal(product) {
      let total = 0
      const productTypeTotal = (product.productType.discounted_price ?? product.productType.producttype_price) * product.qty

      if (this.responseVoucher?.producttype_id == product.productType.producttype_id && this.responseVoucher?.voucher.is_free) {
        return total
      }

      if (this.responseVoucher?.producttype_id == product.productType.producttype_id && this.discountVoucher) {
        const discount = this.discountVoucher
        total -= discount
      }

      total += productTypeTotal

      return total
    },
    numberToRupiah(number) {
      return numberToRupiah(number);
    },
    async validateRequiredFormCheckbox() {
      const resultForm = await this.$store.state.requiredForm?.validate()
      const resultCheckbox = await this.$store.state.requiredCheckbox?.validate()
      if (resultForm.valid && resultCheckbox.valid) this.next()
    },
    async validateRequiredInput() {
      const result = this.validateInput

      if (result || !this.product.is_rombongan) this.next()
    },
    order() {
      this.loadingOrder = true;

      const utc = this.$moment().utcOffset() / 60;

      const orderData = {
        user_email: this.$store.state.user.email,
        is_rombongan: this.product.is_rombongan,
        amount: this.totalCartPrice + this.serviceFee,
        payment_method: this.isFree ? "FR" : this.product.store.payment_gateway === PAYMENT.MIDTRANS ? "MT" : this.selectedPaymentMethod.payment_method,
        payment_name: this.product.store.payment_gateway === PAYMENT.MIDTRANS && !this.isFree ? this.selectedPaymentMethod.payment_method : undefined,
        voucher_code: this.voucherCode ?? undefined,
        send_to_texter: this.sendTexter,
        transaction_fee: this.isFree ? 0 : parseInt(this.selectedPaymentMethod?.totalFee || 0),
        utc: utc,
        orders: [
          {
            store_id: parseInt(this.product.store_id.toString()),
            products: [
              {
                product_id: this.product.product_id,
                product_types: this.cart.map((cartItem) => {
                  return {
                    producttype_id: parseInt(
                      cartItem.productType.producttype_id.toString()
                    ),
                    producttype_price: cartItem.productType.producttype_price,
                    discounted_price: cartItem.productType.discounted_price,
                    qty: cartItem.qty,
                    form: cartItem.productType.form?.map(form => {
                      form.fields.forEach(field => {
                        if (field.infoquestiontype.question_type === 'seller-data') field.value = [null]
                        else if (field.infoquestiontype.question_type === 'datetime') {
                          const formattedDate = moment(field.value).format("YYYY-MM-DD HH:mm");
                          field.value = [formattedDate]
                        }
                        else if (!Array.isArray(field.value)) field.value = [(field.value ?? "").toString()]
                        else field.value.forEach(value => (value ?? "").toString())
                      });
                      return form
                    })
                  };
                }),
              },
            ],
          },
        ],
        additional_info: {
          phone: `${this.$store.state.user.phone[0]}${this.$store.state.user.phone[1]}`,
          full_name: this.$store.state.user.full_name
        }
      };

      this.axios
        .post("order/product", orderData)
        .then((response) => {
          const data = response.data.response;
          const paymentTransaction = data.payment_transaction.pg_response
          const transactionId = data.transaction_id
          this.$store.state.transactionId = transactionId

          if (!response.data.error) {
            this.merchantOrderId = data.transaction_id;
            this.merchantCode = paymentTransaction?.merchantCode;
            if (this.isFree) {
              let callback = process.env.VUE_APP_CALLBACK_DUITKU_MAILER

              if (this.sendTexter) {
                callback = process.env.VUE_APP_CALLBACK_MAILER_TEXTER
              }

              this.axios.post(callback, {
                merchantOrderId: transactionId,
                resultCode: "00"
              })

              this.paymentStatus.code = "00"
              this.$store.state.paymentStatus = "00"

              this.dialogStatus = true;
            }

            if (paymentTransaction) {
              this.paymentURL = this.checkApp ? paymentTransaction?.appRedirect : paymentTransaction?.paymentUrl
              this.openPaymentWindow(this.paymentURL, !this.checkApp)
            }
            this.orderStatus = "process";
          } else {
            this.orderStatus = "error";
          }
        })
        .catch((error) => {
          this.orderStatus = "error";
          var changed = error?.response?.data?.data

          switch (error?.response?.data?.code) {
            case 'payment-method-not-allowed': {
              this.errorMessage = this.$t("cart-detail.order-failed.payment-method-not-allowed");
              this.$store.state.selectedPaymentMethod = null
              this.orderStatus = null
              this.$store.state.orderStatus = null
              this.$emit('fetch-payment-method')
              break
            }
            case 'exceed-max-order': {
              this.$store.state.cart = this.$store.state.cart.map((type) => {
                if (type.productType.producttype_id == changed?.producttype_id) {
                  type.qty = parseInt(changed?.producttype_maximum_order)
                }
                return type
              })
              this.errorMessage = this.$t("cart-detail.order-failed.max-order");
              break;
            }
            case 'below-min-order': {
              this.$store.state.cart = this.$store.state.cart.map((type) => {
                if (type.productType.producttype_id == changed?.producttype_id) {
                  type.qty = parseInt(changed?.producttype_minimum_order)
                }
                return type
              })
              this.errorMessage = this.$t("cart-detail.order-failed.min-order");
              break;
            }
            case 'product-not-published': {
              this.errorMessage = this.$t(
                "cart-detail.order-failed.non-active-product"
              );
              break;
            }
            case 'product-store-missmatch': {
              this.errorMessage = this.$t(
                "cart-detail.order-failed.store-missmatch"
              );
              break;
            }
            case 'product-type-missmatch': {
              this.errorMessage = this.$t(
                "cart-detail.order-failed.product-type-missmatch"
              );
              break;
            }
            case 'out-of-stock': {
              setTimeout(() => {
                const filteredCart = this.$store.state.cart.filter(type => type.productType.producttype_id != changed?.producttype_id);
                if (filteredCart.length > 0) {
                  localStorage.setItem('previousCart', JSON.stringify(filteredCart));
                }
                window.location.reload();
              }, 500);
              this.errorMessage = this.$t(
                "cart-detail.order-failed.out-of-stock"
              );
              break;
            }
            case 'limit-order-exceed': {
              this.errorMessage = this.$t(
                "cart-detail.order-failed.limit-order-exceed",
                { n: parseInt(changed?.limit_order) },
                parseInt(changed?.limit_order)
              );
              break;
            }
            case 'price-changed': {
              this.$store.state.cart = this.$store.state.cart.map((type) => {
                if (type.productType.producttype_id == changed?.producttype_id) {
                  type.productType.producttype_price = parseInt(changed?.producttype_price)
                }
                return type
              })
              this.errorMessage = this.$t("cart-detail.order-failed.price-changed");
              this.$store.state.selectedPaymentMethod = null
              this.orderStatus = null
              this.$store.state.orderStatus = null
              this.$emit('fetch-payment-method')
              break
            }
            case 'discount-changed': {
              this.$store.state.cart = this.$store.state.cart.map((type) => {
                if (type.productType.producttype_id == changed?.producttype_id) {
                  type.productType.discounted_price = changed?.discounted_price
                }
                return type
              })
              this.errorMessage = this.$t("cart-detail.order-failed.discount-changed");
              this.$store.state.selectedPaymentMethod = null
              this.orderStatus = null
              this.$store.state.orderStatus = null
              this.$emit('fetch-payment-method')
              break
            }
            case 'voucher-not-found': {
              this.errorMessage = this.$t("cart-detail.order-failed.voucher-not-found");
              break
            }
            case 'product-not-found': {
              this.errorMessage = this.$t("cart-detail.order-failed.product-not-found");
              setTimeout(() => {
                window.location.reload();
              }, 500);
              break
            }
            case 'product-type-not-found': {
              setTimeout(() => {
                const filteredCart = this.$store.state.cart.filter(type => type.productType.producttype_id != changed?.producttype_id);
                if (filteredCart.length > 0) {
                  localStorage.setItem('previousCart', JSON.stringify(filteredCart));
                }
                window.location.reload();
              }, 500);
              this.errorMessage = this.$t("cart-detail.order-failed.product-type-not-found");
              break
            }
            default: {
              this.errorMessage = this.$t("cart-detail.order-failed.default");
              break;
            }
          }
        })
        .finally(() => {
          this.loadingOrder = false;
          this.confirmation = false
        });
    },
    checkVoucher() {
      const producttypeIds = []


      for (const cartItem of this.cart) {
        producttypeIds.push({
          producttype_id: cartItem.productType.producttype_id,
          qty: cartItem.qty
        })
      }

      this.axios
        .post("voucher/check", {
          voucher_code: this.voucherCode,
          producttype_list: producttypeIds,
        })
        .then((response) => {
          this.showSnackbar("Berhasil memasang kupon/voucher");
          const result = response.data.response;
          this.responseVoucher = result;
          const amountDiscount = result.amount_discount

          this.discountVoucher = amountDiscount

          this.$store.state.voucherCode = this.voucherCode
          this.$store.state.discount = amountDiscount
          this.$store.state.voucher = result

          this.$store.state.selectedPaymentMethod = null
        })
        .catch((error) => {
          this.showSnackbar("Gagal memasang kupon/voucher", true);
          console.log(error);
        });
    },
    showSnackbar(text, error = false) {
      this.snackbar.text = text;
      this.snackbar.show = true;
      this.snackbar.error = error;
    },
    openPaymentWindow(url, openWindow = true) {
      if (openWindow) {
        const windowFeature =
          "toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=500,height=700";
        window.open(url, "targetWindow", windowFeature);
      } else {
        window.location.href = url
      }
    },
    getKeyFromValue(object, value) {
      return Object.keys(object).find((key) => object[key] === value);
    },
    async checkTransactionStatus() {
      const url = this.product.store.payment_gateway == PAYMENT.DUITKU ? `payment/transaction/check` : `payment/trx/${this.getKeyFromValue(PAYMENT, this.product.store.payment_gateway)}/check/${this.$store.state.transactionId}`;
      if (this.product.store.payment_gateway == PAYMENT.DUITKU) {
        await this.axios
          .post(
            url,
            {
              merchantCode: this.merchantCode,
              merchantOrderId: this.merchantOrderId,
            },
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          )
          .then((response) => {
            const statusCode = response.data.statusCode;
            this.paymentStatus.code = statusCode;
            if (statusCode !== "01") {
              this.$store.state.paymentStatus = statusCode;
            }
            this.paymentStatus.show = true;
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        await this.axios
          .get(url)
          .then((response) => {
            const result = response.data.response;

            switch (result.transaction_status) {
              case 'failure':
              case 'expire':
              case 'cancel':
              case 'deny':
              case 'settlement':
              case 'capture':
                this.$store.state.paymentStatus = result.transaction_status === 'capture' || result.transaction_status === 'settlement' ? '00' : '02';
                break
              case 'pending':
                this.paymentStatus.code = '01'
                break
            }
            this.paymentStatus.show = true;
          })
          .catch((error) => {
            console.log(error);
          });
      }
    },
  },
};
</script>